import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { facturasService, centrosCosteService } from '../../services/financialService';

const INVOICE_TYPES = ['Todos', 'Entrada', 'Salida'];
const STATES = ['Todos', 'BORRADOR', 'EMITIDA', 'PENDIENTE', 'COBRADA', 'PAGADA', 'VENCIDA', 'ANULADA'];
const DEPTS = ['Todos', 'Sala de Corte', 'Distribución', 'Tienda General', 'Corporativo'];
const PAGE_SIZES = [25, 50, 100];
const PERIODS = ['Mes', 'Trimestre', 'Año', 'Personalizado'];
const PERIOD_MAP = { 'Mes': 'month', 'Trimestre': 'quarter', 'Año': 'year', 'Personalizado': 'custom' };

const fmt = (n) => (typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00');
const fmtEur = (n) => `${fmt(n)} €`;

const ESTADO_COLORS = {
  BORRADOR: '#60A5FA', EMITIDA: '#D6EF4E', PENDIENTE: '#FBBF24',
  COBRADA: '#34D399', PAGADA: '#34D399', VENCIDA: '#F87171', ANULADA: 'rgba(255,255,255,0.30)',
};

const FacturaFormModal = ({ onClose, onSave, centros }) => {
  const [form, setForm] = useState({
    tipo: 'RECIBIDA', numero: '', fecha: new Date()?.toISOString()?.split('T')?.[0],
    fecha_vencimiento: '', emisor: '', emisor_cif: '', receptor: '', receptor_cif: '',
    departamento: '', centro_coste_id: '', base_imponible: '', iva_importe: '',
    total: '', cobrado_pagado: 0, estado: 'PENDIENTE', forma_pago: '', observaciones: '',
  });
  const [saving, setSaving] = useState(false);
  const inputCls = 'w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/80 outline-none bg-transparent';
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        ...form,
        base_imponible: parseFloat(form?.base_imponible) || 0,
        iva_importe: parseFloat(form?.iva_importe) || 0,
        total: parseFloat(form?.total) || 0,
        cobrado_pagado: parseFloat(form?.cobrado_pagado) || 0,
        centro_coste_id: form?.centro_coste_id || null,
        fecha_vencimiento: form?.fecha_vencimiento || null,
        forma_pago: form?.forma_pago || null,
      });
      onClose();
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-oranienbaum font-bold text-white text-lg">Nueva Factura</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Tipo</label>
            <select value={form?.tipo} onChange={e => setForm(p => ({ ...p, tipo: e?.target?.value }))} className={inputCls} style={inputStyle}>
              <option value="RECIBIDA" style={{ background: '#141414' }}>RECIBIDA (Entrada)</option>
              <option value="EMITIDA" style={{ background: '#141414' }}>EMITIDA (Salida)</option>
            </select>
          </div>
          {[
            { label: 'Número', key: 'numero', type: 'text' },
            { label: 'Fecha *', key: 'fecha', type: 'date' },
            { label: 'Fecha Vencimiento', key: 'fecha_vencimiento', type: 'date' },
            { label: 'Emisor', key: 'emisor', type: 'text' },
            { label: 'CIF Emisor', key: 'emisor_cif', type: 'text' },
            { label: 'Receptor', key: 'receptor', type: 'text' },
            { label: 'Base Imponible (€)', key: 'base_imponible', type: 'number' },
            { label: 'IVA (€)', key: 'iva_importe', type: 'number' },
            { label: 'Total (€)', key: 'total', type: 'number' },
            { label: 'Cobrado/Pagado (€)', key: 'cobrado_pagado', type: 'number' },
          ]?.map(f => (
            <div key={f?.key}>
              <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">{f?.label}</label>
              <input type={f?.type} value={form?.[f?.key]} onChange={e => setForm(p => ({ ...p, [f?.key]: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </div>
          ))}
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Estado</label>
            <select value={form?.estado} onChange={e => setForm(p => ({ ...p, estado: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {['BORRADOR', 'EMITIDA', 'PENDIENTE', 'COBRADA', 'PAGADA', 'VENCIDA', 'ANULADA']?.map(s => <option key={s} value={s} style={{ background: '#141414' }}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Centro de Coste</label>
            <select value={form?.centro_coste_id} onChange={e => setForm(p => ({ ...p, centro_coste_id: e?.target?.value }))} className={inputCls} style={inputStyle}>
              <option value="" style={{ background: '#141414' }}>— Sin asignar —</option>
              {centros?.map(c => <option key={c?.id} value={c?.id} style={{ background: '#141414' }}>{c?.nombre}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50" style={{ background: '#D6EF4E' }}>
            {saving ? 'Guardando...' : 'Guardar Factura'}
          </button>
        </div>
      </div>
    </div>
  );
};

const FinFacturacion = () => {
  const [invoiceType, setInvoiceType] = useState('Todos');
  const [state, setState] = useState('Todos');
  const [period, setPeriod] = useState('Mes');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [showForm, setShowForm] = useState(false);

  const [facturas, setFacturas] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const periodKey = PERIOD_MAP?.[period] || 'month';
      const [facturasData, kpisData, centrosData] = await Promise.all([
        facturasService?.getAll({
          period: periodKey,
          dateFrom: period === 'Personalizado' ? dateFrom : undefined,
          dateTo: period === 'Personalizado' ? dateTo : undefined,
          tipo: invoiceType !== 'Todos' ? invoiceType : undefined,
          estado: state !== 'Todos' ? state : undefined,
          search: search || undefined,
        }),
        facturasService?.getKPIs({ period: periodKey, dateFrom: period === 'Personalizado' ? dateFrom : undefined, dateTo: period === 'Personalizado' ? dateTo : undefined }),
        centrosCosteService?.getAll(),
      ]);
      setFacturas(facturasData);
      setKpis(kpisData);
      setCentros(centrosData);
    } catch (err) {
      setError(err?.message || 'Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  }, [period, dateFrom, dateTo, invoiceType, state, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const kpiCards = [
    { label: 'Facturación Emitida', value: fmtEur(kpis?.facturacionEmitida || 0), icon: 'FileOutput', color: '#D6EF4E' },
    { label: 'Facturación Recibida', value: fmtEur(kpis?.facturacionRecibida || 0), icon: 'FileInput', color: '#60A5FA' },
    { label: 'Base Imponible Total', value: fmtEur(kpis?.baseImponibleTotal || 0), icon: 'Calculator', color: '#34D399' },
    { label: 'IVA Soportado', value: fmtEur(kpis?.ivaSoportado || 0), icon: 'ArrowDownCircle', color: '#FBBF24' },
    { label: 'IVA Repercutido', value: fmtEur(kpis?.ivaRepercutido || 0), icon: 'ArrowUpCircle', color: '#A78BFA' },
    { label: 'Facturas Pendientes', value: kpis?.facturasPendientes || 0, icon: 'Clock', color: '#FBBF24' },
    { label: 'Facturas Vencidas', value: kpis?.facturasVencidas || 0, icon: 'AlertTriangle', color: '#F87171' },
    { label: 'Importe Cobrado', value: fmtEur(kpis?.importeCobrado || 0), icon: 'CheckCircle', color: '#34D399' },
    { label: 'Importe Pagado', value: fmtEur(kpis?.importePagado || 0), icon: 'CheckCircle', color: '#60A5FA' },
    { label: 'Importe Pendiente', value: fmtEur(kpis?.importePendiente || 0), icon: 'Hourglass', color: '#F87171' },
  ];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {showForm && <FacturaFormModal onClose={() => setShowForm(false)} onSave={async (p) => { await facturasService?.create(p); loadData(); }} centros={centros} />}
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-xs font-oranienbaum text-white/30">
        <Icon name="Home" size={12} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">Finanzas Corporativas</span>
        <span>/</span>
        <span className="text-white/50">Facturación General</span>
      </div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)' }}>
            <Icon name="FileText" size={20} className="text-[#D6EF4E]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl tracking-wide">Facturación General</h1>
            <p className="font-oranienbaum text-white/40 text-xs">Repositorio consolidado · Todas las facturas de la empresa</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black transition-all hover:opacity-90"
            style={{ background: '#D6EF4E' }}>
            <Icon name="Plus" size={14} />
            Nueva Factura
          </button>
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="RefreshCw" size={13} />
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
        </div>
      )}
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-2xl p-4 flex flex-col gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
              <Icon name={kpi?.icon} size={14} style={{ color: kpi?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white/80 text-lg">{loading ? '...' : kpi?.value}</p>
            <p className="font-oranienbaum text-white/35 text-[10px] leading-tight">{kpi?.label}</p>
          </div>
        ))}
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl flex-1 min-w-[200px]"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Icon name="Search" size={14} className="text-white/30" />
          <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Buscar por número, emisor, NIF..."
            className="bg-transparent text-xs font-oranienbaum text-white/70 placeholder-white/25 outline-none flex-1" />
        </div>
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {INVOICE_TYPES?.map(t => (
            <button key={t} onClick={() => setInvoiceType(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all ${invoiceType === t ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
              style={invoiceType === t ? { background: '#D6EF4E' } : {}}>
              {t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {PERIODS?.map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all ${period === p ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
              style={period === p ? { background: '#D6EF4E' } : {}}>
              {p}
            </button>
          ))}
        </div>
        {period === 'Personalizado' && (
          <>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e?.target?.value)}
              className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none bg-transparent"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} />
            <input type="date" value={dateTo} onChange={e => setDateTo(e?.target?.value)}
              className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none bg-transparent"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} />
          </>
        )}
        <select value={state} onChange={e => setState(e?.target?.value)}
          className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none cursor-pointer"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {STATES?.map(s => <option key={s} value={s} style={{ background: '#111' }}>{s}</option>)}
        </select>
      </div>
      {/* Table */}
      <div className="rounded-2xl overflow-hidden mb-4"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                {['Tipo', 'Número', 'Fecha', 'Emisor/Receptor', 'Centro Coste', 'Base', 'IVA', 'Total', 'Pendiente', 'Vencimiento', 'Estado']?.map(col => (
                  <th key={col} className="px-3 py-3 text-left">
                    <span className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" />
                    <span className="font-oranienbaum text-white/30 text-xs">Cargando facturas...</span>
                  </div>
                </td></tr>
              ) : facturas?.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Icon name="FileText" size={22} className="text-white/20" />
                    </div>
                    <p className="font-oranienbaum text-white/30 text-sm">No hay facturas registradas</p>
                    <button onClick={() => setShowForm(true)} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black" style={{ background: '#D6EF4E' }}>
                      Registrar primera factura
                    </button>
                  </div>
                </td></tr>
              ) : facturas?.slice(0, pageSize)?.map((f, i) => (
                <tr key={f?.id || i} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                  <td className="px-3 py-3">
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum"
                      style={{ background: f?.tipo === 'EMITIDA' ? 'rgba(214,239,78,0.12)' : 'rgba(96,165,250,0.12)', color: f?.tipo === 'EMITIDA' ? '#D6EF4E' : '#60A5FA', border: `1px solid ${f?.tipo === 'EMITIDA' ? 'rgba(214,239,78,0.25)' : 'rgba(96,165,250,0.25)'}` }}>
                      {f?.tipo === 'EMITIDA' ? 'Salida' : 'Entrada'}
                    </span>
                  </td>
                  <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{f?.numero || '—'}</span></td>
                  <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{f?.fecha}</span></td>
                  <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-xs max-w-[140px] truncate block">{f?.emisor || f?.receptor || '—'}</span></td>
                  <td className="px-3 py-3"><span className="font-oranienbaum text-white/40 text-[10px]">{f?.centro_coste?.nombre || '—'}</span></td>
                  <td className="px-3 py-3 text-right"><span className="font-oranienbaum text-white/60 text-xs">{fmtEur(f?.base_imponible)}</span></td>
                  <td className="px-3 py-3 text-right"><span className="font-oranienbaum text-white/40 text-xs">{fmtEur(f?.iva_importe)}</span></td>
                  <td className="px-3 py-3 text-right"><span className="font-oranienbaum font-semibold text-white/80 text-xs">{fmtEur(f?.total)}</span></td>
                  <td className="px-3 py-3 text-right"><span className="font-oranienbaum text-[#F87171] text-xs">{fmtEur(f?.pendiente)}</span></td>
                  <td className="px-3 py-3"><span className="font-oranienbaum text-white/40 text-xs">{f?.fecha_vencimiento || '—'}</span></td>
                  <td className="px-3 py-3">
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum"
                      style={{ background: `${ESTADO_COLORS?.[f?.estado] || '#60A5FA'}15`, color: ESTADO_COLORS?.[f?.estado] || '#60A5FA', border: `1px solid ${ESTADO_COLORS?.[f?.estado] || '#60A5FA'}30` }}>
                      {f?.estado}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {facturas?.length > 0 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-white/5">
            <span className="font-oranienbaum text-white/30 text-xs">{facturas?.length} facturas</span>
            <select value={pageSize} onChange={e => setPageSize(Number(e?.target?.value))}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/50 outline-none cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {PAGE_SIZES?.map(s => <option key={s} value={s} style={{ background: '#111' }}>{s} por página</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinFacturacion;
